import { useRef, useState, useLayoutEffect, useMemo, memo, useCallback } from 'react';
import { ChartDay } from './components/ChartDay';
import { ChartSvg } from './components/ChartSvg';
import { useDebouncedResizeObserver } from './useDebouncedResizeObserver';

import * as S from './Chart.styled';

// chart height and min day width in px
const chartHeight = 280;
const minDayWidth = 17;

export const Chart = memo(
  ({
    dataPerDay,
    colors,
    metrics,
    selectedMetrics,
    selectedMetricsSelector, // used to skip days re-render
    isChartHidden,
    withNotes,
    withModal,
    isEvents,
    currentEventInd,
  }) => {
    const container = useRef(null);
    const [width, setWidth] = useState(container?.current?.offsetWidth || 0);
    const [step, setStep] = useState(() =>
      width ? getTruncStep(width, minDayWidth, dataPerDay.size) : 1,
    );

    const daysArray = useMemo(() => [...dataPerDay.keys()], [dataPerDay]);
    const dataForEvents = useMemo(
      () => formatDataForEvents(dataPerDay, currentEventInd, isEvents),
      [dataPerDay, currentEventInd, isEvents],
    );
    const currentEventTitle = useMemo(() => {
      if (!daysArray.length || !isEvents) return '';

      const firstDay = dataPerDay.get(daysArray[0]);
      const eventsArr = Object.values(firstDay.events);

      return eventsArr[currentEventInd]?.label;
    }, [isEvents, daysArray, dataPerDay, currentEventInd]);

    useLayoutEffect(() => {
      if (container.current) {
        if (container.current.offsetWidth !== width) {
          setWidth(container.current.offsetWidth);
        }
      }
    }, [isChartHidden]);

    // handle zoom and screen resize to update step
    useDebouncedResizeObserver(
      container,
      (rect) => {
        handleResize(rect.width);
      },
      200,
    );

    const handleResize = useCallback(
      (newWidth) => {
        setWidth(newWidth);
        setStep(newWidth ? getTruncStep(newWidth, minDayWidth, dataPerDay.size) : 1);
      },
      [dataPerDay.size, minDayWidth],
    );

    // calculate max for each metric for the given time gap
    const maxDataValues = useMemo(
      () => findMaxValues(dataForEvents, metrics),
      [dataForEvents, metrics],
    );
    // set up Y axis maximums
    const maxValues = useMemo(() => getScales(maxDataValues, metrics), [maxDataValues, metrics]);

    // get current number of selected tabs
    const numOfTubsSelected = Object.keys(selectedMetrics).filter((metric) => {
      if (!isEvents && (metric === 'events' || metric === 'avg_cpa')) return false;
      return selectedMetrics[metric];
    }).length;
    // if 1 or 2 tabs selected - show vertical scales
    const isVerticalScales = numOfTubsSelected === 1 || numOfTubsSelected === 2;
    // calculate which metric to show for both sides
    const { rightScale, leftScale } = getVerticalScales(
      metrics,
      selectedMetrics,
      numOfTubsSelected,
    );

    return (
      !isChartHidden && (
        <S.OuterContainer>
          <S.Container
            style={{ minHeight: `${chartHeight}px` }}
            onMouseOver={step === 1 ? () => {} : handleDayHover}
            onMouseOut={handleDayLeave}
          >
            {!!width &&
              daysArray.length > 0 &&
              daysArray.map((day, index) => (
                <ChartDay
                  key={day}
                  day={day}
                  tooltipPlacement={index < daysArray.length / 2 ? 'left' : 'right'}
                  dayData={dataForEvents.get(day)}
                  height={chartHeight}
                  maxValues={maxValues}
                  metrics={metrics}
                  colors={colors}
                  isDateShown={isDateShown(index, daysArray.length, step)}
                  selectedMetricsSelector={selectedMetricsSelector}
                  withNotes={withNotes}
                  withModal={withModal}
                  currentEventTitle={currentEventTitle}
                />
              ))}
            <S.ChartWrap style={{ height: `${chartHeight}px` }} ref={container}>
              {!!width &&
                metrics
                  .filter((metric) => selectedMetrics[metric])
                  .map((metric) => (
                    <ChartSvg
                      key={metric}
                      data={dataForEvents}
                      maxY={maxValues[metric]}
                      width={width}
                      height={chartHeight}
                      metric={metric}
                      color={colors[metric].main}
                    />
                  ))}
              {isVerticalScales && (
                <>
                  {leftScale && (
                    <S.ScaleLeft>
                      <S.ScaleNumber>{formatNumber(maxValues[leftScale])}</S.ScaleNumber>
                      <S.ScaleNumber>{formatNumber(+maxValues[leftScale] / 2)}</S.ScaleNumber>
                      <S.ScaleNumber>0</S.ScaleNumber>
                    </S.ScaleLeft>
                  )}
                  {rightScale && (
                    <S.ScaleRight>
                      <S.ScaleNumber>{formatNumber(maxValues[rightScale])}</S.ScaleNumber>
                      <S.ScaleNumber>{formatNumber(+maxValues[rightScale] / 2)}</S.ScaleNumber>
                      <S.ScaleNumber>0</S.ScaleNumber>
                    </S.ScaleRight>
                  )}
                </>
              )}
            </S.ChartWrap>
          </S.Container>
        </S.OuterContainer>
      )
    );
  },
);

function formatDataForEvents(data, ind, isEvents) {
  if (!isEvents) return data;
  const newData = new Map();

  for (let [key, value] of data) {
    const selectedId = Object.keys(value.events)[ind || 0];

    const newValue = {
      ...value,
      events: value.events[selectedId].value,
      avg_cpa: value.avg_cpa[selectedId],
    };

    newData.set(key, newValue);
  }

  return newData;
}

/* 
  these two functions hide dates if day is too narrow to show them all.
  current possible min-width of the chart is 1150px and max number of days if 180
  for smaller width or more days - consider hiding siblings (with toggleSiblingsClass)
  in a loop (untill 3 steps > minDayWidth) where 3 steps - prev, hovered and next
*/
function handleDayHover(e) {
  const hovered = e.target.closest('.day');
  if (!hovered) return;

  toggleSiblingsClass(hovered, 'sibling-hover', true);
}

function handleDayLeave(e) {
  const hovered = e.target.closest('.day');
  if (!hovered) return;

  toggleSiblingsClass(hovered, 'sibling-hover', false);
}

function toggleSiblingsClass(elem, className, isAdd) {
  const prev = elem.previousElementSibling;
  const next = elem.nextElementSibling;

  if (isAdd) {
    prev?.classList.add(className);
    next?.classList.add(className);
  } else {
    prev?.classList.remove(className);
    next?.classList.remove(className);
  }
}

function isDateShown(ind, arrLength, step) {
  if (step === 1) return true;
  // hide step before the last one
  if (ind === arrLength - 2 && ind % step === 0) return false;

  // if we hide next one (second from the end)
  if (ind === arrLength - 3 && (ind + 1) % step === 0) {
    // show this one (3rd from the end) instead, if it's not shown and two prev ones are not shown
    return (
      ind % step === 0 || (ind % step !== 0 && (ind - 1) % step !== 0 && (ind - 2) % step !== 0)
    );
  }

  return ind % step === 0 || ind === 0 || ind === arrLength - 1;
}

// function parseDate(dateStr) {
//   const [year, month, day] = dateStr.split(',').map(Number);
//   return new Date(year, month - 1, day);
// }

// function getDayDiff(dateStr1, dateStr2) {
//   const msPerDay = 1000 * 60 * 60 * 24;
//   const d1 = parseDate(dateStr1);
//   const d2 = parseDate(dateStr2);
//   return Math.round((d2 - d1) / msPerDay);
// }

// function getPace(a, b) {
//   if (!a || !b) return 1;
//   return getDayDiff(a, b);
// }

// function isEvenDaysPace([a, b, c]) {
//   return getDayDiff(a, b) === getDayDiff(b, c);
// }

function getTruncStep(width, minWidth, fullDaysNum) {
  let daysNum = fullDaysNum;
  let step = 1;

  while (!validateDateRange(daysNum, width, minWidth)) {
    step++;
    daysNum = fullDaysNum / step;
  }

  return step;
}

// function truncateData(data, days) {
//   if (data.size === days.length) return data;

//   return new Map(Array.from(data).filter(([key]) => days.includes(key)));
// }

// function truncateDays(width, minWidth, days) {
//   const lastDay = days[days.length - 1];

//   while (!validateDateRange(days.length, width, minWidth)) {
//     days = days.filter((_, ind) => ind % 2 === 0);
//   }

//   return lastDay === days[days.length - 1] ? days : [...days, lastDay];
// }

// return a name of metric (or null) for left and right scales
function getVerticalScales(metrics, selectedMetrics, numOfTubsSelected) {
  let rightScale = null;
  let leftScale = null;

  if (numOfTubsSelected === 1 || numOfTubsSelected === 2) {
    let currentInd;

    metrics.forEach((metric, ind) => {
      if (selectedMetrics[metric]) {
        if (numOfTubsSelected === 1) {
          rightScale = metric;
        } else {
          if (currentInd === undefined) {
            currentInd = ind;
            rightScale = metric;
          } else {
            if (ind > currentInd) {
              leftScale = rightScale;
              rightScale = metric;
            } else {
              leftScale = metric;
            }
          }
        }
      }
    });
  }

  return { rightScale, leftScale };
}

// return true if the data size fits the chart container width
function validateDateRange(days, width, minDayWidth) {
  return width / days > minDayWidth;
}

// function calculates max values for the top of each Y axis.
function getScales(maxValues, metrics) {
  // initialize the object structure, props must match maxValues
  let yScales = {};

  metrics.forEach((metric) => {
    if (metric === 'cost') {
      //  1.2 to show closer to the middle
      yScales[metric] = roundNumber(maxValues[metric] * 1.2);
    } else if (metric === 'avg_cpi') {
      // avg_cpi can be any, 4 makes the line take 25% of the bottom space (100% / 4)
      yScales[metric] = roundNumber(maxValues[metric] * 4);
    } else {
      // all other just rounded
      yScales[metric] = roundNumber(maxValues[metric]);
    }
  });

  // assuming that clicks are 30 times less than impressions
  yScales.clicks = roundNumber(yScales.impressions / 30);
  // ... and installs are 10 times less than clicks
  yScales.installs = roundNumber(yScales.clicks / 10);

  // check if any value exceeds maximum or 10 times smaller than data max
  Object.keys(yScales).forEach((metric) => {
    const isTooLarge = yScales[metric] < maxValues[metric];
    const isTooSmall = yScales[metric] / maxValues[metric] > 10;

    if (isTooLarge || isTooSmall) yScales[metric] = roundNumber(maxValues[metric]);
  });

  return yScales;
}

function roundNumber(num, rounder) {
  let roundedNum;

  if (rounder) return Math.ceil(num / rounder) * rounder;

  if (num < 100) {
    roundedNum = Math.ceil(num / 10) * 10;
  } else if (num < 1000) {
    roundedNum = Math.ceil(num / 100) * 100;
  } else if (num < 10_000) {
    roundedNum = Math.ceil(num / 1000) * 1000;
  } else if (num < 100_000) {
    roundedNum = Math.ceil(num / 10_000) * 10_000;
  } else if (num < 1_000_000) {
    roundedNum = Math.ceil(num / 10_000) * 10_000;
  } else {
    roundedNum = Math.ceil(num / 50_000) * 50_000;
  }

  return roundedNum;
}

function findMaxValues(daysDataMap, metrics) {
  let maxValues = {};

  metrics.forEach((metric) => {
    maxValues[metric] = -Infinity;
  });

  daysDataMap.forEach((value) => {
    for (const [metric, num] of Object.entries(value)) {
      if (maxValues[metric] < num) maxValues[metric] = num;
    }
  });

  return maxValues;
}

function formatNumber(number) {
  const suffixes = ['', 'K', 'M', 'B']; // thousands, millions, billions

  const suffixIndex = Math.floor(Math.log10(number) / 3);
  const scaledNumber = suffixes[suffixIndex] ? number / Math.pow(10, suffixIndex * 3) : number;
  let formattedNumber = scaledNumber?.toFixed(2);

  if (formattedNumber?.indexOf('.') !== -1) {
    formattedNumber = formattedNumber?.replace(/\.?0*$/, '');
  }

  return formattedNumber + (suffixes[suffixIndex] ? suffixes[suffixIndex] : '');
}

Chart.displayName = 'Chart';
